/**
 * Whether the user is on a page the step belongs to. A step names its route
 * names and, when one route name serves many pages (e.g. the inbox form
 * route for every channel type), the route params it needs.
 *
 * @param {{routes: string[], routeParams?: Object}|null} step
 * @param {{name?: string, params?: Object}} route
 */
export const stepMatchesRoute = (step, route) => {
  if (!step || !step.routes.includes(route?.name)) return false;
  return Object.entries(step.routeParams || {}).every(
    ([key, value]) => String(route.params?.[key]) === String(value)
  );
};

/**
 * Takes the user to the page a guide step lives on, unless they are already
 * there or the step can't be navigated to (it needs an id only the user's
 * own action produces, like a freshly created inbox — mark those
 * `navigable: false`). Resolves to true when the user ends up on the step's
 * page, false when navigation was skipped or blocked (a route guard
 * redirect, say) — the overlay simply stays hidden until the user arrives.
 *
 * @param {Object} args
 * @param {import('vue-router').default} args.router
 * @param {{name?: string, params?: Object}} args.route current route
 * @param {Object|null} args.step
 * @param {number|string} args.accountId
 */
export const goToStepRoute = async ({ router, route, step, accountId }) => {
  if (!step) return false;
  if (stepMatchesRoute(step, route)) return true;
  if (step.navigable === false) return false;
  try {
    await router.push({
      name: step.routes[0],
      params: { accountId, ...step.routeParams },
    });
    return true;
  } catch {
    return false;
  }
};

/**
 * The step to resume a paused guide from: the current step, or the nearest
 * earlier one the user can be taken to (a step that needs an id produced by
 * their own action can't be navigated to directly).
 *
 * @param {Array<{navigable?: boolean}>} steps
 * @param {number} fromIndex
 */
export const resumeIndex = (steps, fromIndex) =>
  steps.reduce(
    (found, step, index) =>
      index <= fromIndex && step.navigable !== false ? index : found,
    0
  );

// Name of the route for the new onboarding list (routes/dashboard/
// onboardingGuides). Lives here, not in the routes file, so components can use
// it without importing the router.
export const ONBOARDING_GUIDES_ROUTE = 'onboarding_guides';
